import { apiFetch, apiGet, apiPost, apiPut, apiPatch, apiDelete } from '../utils/api';
/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useToast } from './ToastContext';

const PIMContext = createContext();

export const usePIM = () => {
    const context = useContext(PIMContext);
    if (!context) throw new Error('usePIM must be used within a PIMProvider');
    return context;
};

const TEST_THEME = {
    theme_bg_main: '#2e1003',
    theme_bg_panel: '#451a03',
    theme_bg_surface: '#78350f',
    theme_text_primary: '#fef3c7',
    theme_text_muted: '#d97706',
    theme_border: '#d97706',
    theme_bg_content: '#2e1003',
    theme_bg_image: ''
};

const applyTheme = (themeSettings) => {
    const root = document.documentElement;
    if (themeSettings.theme_bg_main) root.style.setProperty('--color-bg-main', themeSettings.theme_bg_main);
    if (themeSettings.theme_bg_panel) root.style.setProperty('--color-bg-panel', themeSettings.theme_bg_panel);
    if (themeSettings.theme_bg_surface) root.style.setProperty('--color-bg-surface', themeSettings.theme_bg_surface);
    if (themeSettings.theme_text_primary) root.style.setProperty('--color-text-primary', themeSettings.theme_text_primary);
    if (themeSettings.theme_text_muted) root.style.setProperty('--color-text-muted', themeSettings.theme_text_muted);
    if (themeSettings.theme_border) root.style.setProperty('--color-border', themeSettings.theme_border);
    if (themeSettings.theme_bg_content) root.style.setProperty('--color-bg-content', themeSettings.theme_bg_content);
    if (themeSettings.theme_bg_image) {
        root.style.setProperty('--theme-bg-image', `url('${themeSettings.theme_bg_image}')`);
    } else {
        root.style.removeProperty('--theme-bg-image');
    }
};

export const PIMProvider = ({ children }) => {
    const { toast } = useToast();
    const [activeTab, setActiveTab] = useState('properties');
    const [user, setUser] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);
    
    // Global Particle Ring State (for persistent background sync)
    const [ringState, setRingState] = useState({
        targetRadiusMultiplier: 1.0,
        flareIntensity: 0,
        flareColor: [220, 20, 20],
        isSuccess: false,
        flareTriggerTime: 0
    });

    const updateRingState = useCallback((news) => {
        setRingState(prev => ({ ...prev, ...news }));
    }, []);

    // Auth Initialization
    useEffect(() => {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (token && storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (e) {
                console.error("Failed to parse user from storage", e);
                localStorage.removeItem('token');
                localStorage.removeItem('user');
            }
        }
        setAuthLoading(false);
    }, []);

    const logout = useCallback(() => {
        console.log("Logging out...");
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setAuthLoading(false);
        updateRingState({
            isSuccess: false,
            flareIntensity: 0,
            targetRadiusMultiplier: 1.0,
            flareColor: [220, 20, 20]
        });
        // window.location.href = '/'; // optional, maybe we want React to re-render App.jsx naturally
    }, [updateRingState]);

    useEffect(() => {
        const handleUnauthorized = () => logout();
        window.addEventListener('pim:unauthorized', handleUnauthorized);
        return () => window.removeEventListener('pim:unauthorized', handleUnauthorized);
    }, [logout]);


    const apiFetch = useCallback(async (url, options = {}) => {
        const token = localStorage.getItem('token');
        const headers = { ...options.headers };
        
        if (token && token !== 'null' && token !== 'undefined') {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const res = await fetch(url, { ...options, headers });
        if (res.status === 401) {
            console.warn("API returned 401 Unauthorized. Forcing logout.");
            logout();
            // We can throw an error or return res to let the caller handle it.
            // Returning res to avoid breaking chains, but the app will rerender to login.
        }
        return res;
    }, [logout]);

    const login = async (username, password) => {
        try {
            console.log("Attempting login for:", username);
            const res = await fetch('/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });

            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || 'Login failed');
            }

            const data = await res.json();
            console.log("Login successful:", data);

            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(data.user));
            setUser(data.user);
            return data.user;
        } catch (err) {
            console.error("Login Error:", err);
            throw err;
        }
    };
    const [dbMode, setDbMode] = useState('prod');

    const fetchDbMode = async () => {
        try {
            const res = await apiFetch('/api/admin/db-mode');
            const data = await res.json();
            if (data.success) {
                setDbMode(data.db_mode);
            }
        } catch (err) {
            console.error("Failed to fetch DB mode", err);
        }
    };

    useEffect(() => {
        fetchDbMode();
    }, []);

    // Mock Data - Properties
    const [properties, setProperties] = useState([]);

    // Mock Data - Charges
    const [charges, setCharges] = useState([]);

    // Master Data
    // Master Data
    const [availableProperties, setAvailableProperties] = useState([]);
    const [propertyGroups, setPropertyGroups] = useState([]);
    const [propertyTypes, setPropertyTypes] = useState([]);
    const [languageGroups, setLanguageGroups] = useState([]);
    const [availableLanguages, setAvailableLanguages] = useState([]);

    // Packaging Logistics Data
    const [packagingProperties, setPackagingProperties] = useState([]);
    const [availablePackagingProperties, setAvailablePackagingProperties] = useState([]);
    const [packagingGroups, setPackagingGroups] = useState([]);
    const [packagingTypes, setPackagingTypes] = useState([]);

    const [colors, setColors] = useState([]);
    const [categories, setCategories] = useState([]);
    const [statuses, setStatuses] = useState([]);

    // Product Data (Global State for Form)
    const [productData, setProductData] = useState({
        model: '',
        sku: '',
        ean: '',
        description: '',
        notes: '',
        color: '',
        category: '',
        factory_model: '',
        status: '',
        stock: 0,
        isAccessory: false,
        product_types: [], // New Field
        image: null
    });

    const [activeCharge, setActiveCharge] = useState(null);
    const [chargeLanguages, setChargeLanguages] = useState([]);
    const [productImages, setProductImages] = useState([]);
    const [allChargeDocuments, setAllChargeDocuments] = useState([]);
    const [resourceTypes, setResourceTypes] = useState([]);
    const [components, setComponents] = useState([]); // New State

    // Derived State for Software and Documents
    const softwareResources = React.useMemo(() => {
        try {
            if (!Array.isArray(resourceTypes) || !Array.isArray(allChargeDocuments)) {
                return [];
            }

            const softwareTypes = resourceTypes
                .filter(t => t.category === 'Software')
                .map(t => (t.name || '').trim().toLowerCase());

            return allChargeDocuments
                .filter(d => {
                    const safeType = (d.type || '').trim().toLowerCase();
                    return softwareTypes.includes(safeType) || safeType === 'software';
                })
                .map(doc => {
                    const safeType = (doc.type || '').trim().toLowerCase();
                    const typeDef = resourceTypes.find(t => (t.name || '').trim().toLowerCase() === safeType);
                    return {
                        ...doc,
                        type_sort_order: typeDef ? typeDef.sort_order : 999,
                        type_color: typeDef ? typeDef.color : null,
                        type_icon: typeDef ? typeDef.icon : null,
                        type_description: typeDef ? typeDef.description : null
                    };
                });
        } catch (err) {
            console.error("PIMContext: Error in softwareResources", err);
            return [];
        }
    }, [allChargeDocuments, resourceTypes]);

    const documentResources = React.useMemo(() => {
        try {
            if (!Array.isArray(resourceTypes) || !Array.isArray(allChargeDocuments)) {
                return [];
            }

            const softwareTypes = resourceTypes
                .filter(t => t.category === 'Software')
                .map(t => (t.name || '').trim().toLowerCase());

            // Documents are everything else
            return allChargeDocuments
                .filter(d => {
                    const safeType = (d.type || '').trim().toLowerCase();
                    return !softwareTypes.includes(safeType) && safeType !== 'software';
                })
                .map(doc => {
                    const safeType = (doc.type || '').trim().toLowerCase();
                    const typeDef = resourceTypes.find(t => (t.name || '').trim().toLowerCase() === safeType);
                    return {
                        ...doc,
                        type_sort_order: typeDef ? typeDef.sort_order : 999,
                        type_color: typeDef ? typeDef.color : null,
                        type_icon: typeDef ? typeDef.icon : null,
                        type_description: typeDef ? typeDef.description : null
                    };
                });
        } catch (err) {
            console.error("PIMContext: Error in documentResources", err);
            return [];
        }
    }, [allChargeDocuments, resourceTypes]);

    // --- Dynamic Widget State ---
    const [leftWidget, setLeftWidget] = useState('ProductForm');
    const [middleWidget, setMiddleWidget] = useState('MiddlePanel');
    const [rightWidget, setRightWidget] = useState('PackageContentsPanel');

    // Shared Widget State
    const [selectedPalletArrangement, setSelectedPalletArrangement] = useState(null);
    const [palletCalculationResults, setPalletCalculationResults] = useState([]);
    const [isPalletMode, setIsPalletMode] = useState(false);
    const [palletMiddleWidget, setPalletMiddleWidget] = useState('Pallet3DVisualizer');
    const [palletRightWidget, setPalletRightWidget] = useState('PalletControls');
    const [isInnerPackMode, setIsInnerPackMode] = useState(false);
    const [innerPackCalculationResult, setInnerPackCalculationResult] = useState(null);

    const resetLayout = () => {
        setLeftWidget('ProductForm');
        setMiddleWidget('MiddlePanel');
        setRightWidget('PackageContentsPanel');
        setSelectedPalletArrangement(null);
        setPalletCalculationResults([]);
        setIsPalletMode(false);
        setIsInnerPackMode(false);
        setInnerPackCalculationResult(null);
        setPalletMiddleWidget('Pallet3DVisualizer');
        setPalletRightWidget('PalletControls');
    };

    const fetchDashboardOverview = async () => {
        try {
            const res = await apiFetch('/api/dashboard/projects');
            if (res.ok) {
                return await res.json();
            }
            return [];
        } catch (err) {
            console.error("Failed to fetch dashboard overview", err);
            return [];
        }
    };

    // --- Fetchers ---

    const fetchPropertyTypes = async () => {
        try {
            const res = await apiFetch('/api/master-property-types');
            if (res.ok) setPropertyTypes(await res.json());
        } catch (err) { console.error("Failed to fetch property types", err); }
    };

    // Property Groups
    const fetchPropertyGroups = async () => {
        try {
            const res = await apiFetch('/api/master/property-groups');
            if (res.ok) setPropertyGroups(await res.json());
        } catch (err) { console.error("Failed to fetch property groups", err); }
    };

    const addPropertyGroup = async (data) => {
        await apiFetch('/api/master/property-groups', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        fetchPropertyGroups();
    };
    const updatePropertyGroup = async (id, data) => {
        await apiFetch(`/api/master/property-groups/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        fetchPropertyGroups();
    };
    const deletePropertyGroup = async (id) => {
        await apiFetch(`/api/master/property-groups/${id}`, { method: 'DELETE' });
        fetchPropertyGroups();
    };

    const fetchMasterProperties = async () => {
        try {
            const res = await apiFetch('/api/master-properties');
            if (res.ok) setAvailableProperties(await res.json());
        } catch (err) { console.error("Failed to fetch master properties", err); }
    };

    const fetchLanguageGroups = async () => {
        try {
            const res = await apiFetch('/api/master-language-groups');
            if (res.ok) setLanguageGroups(await res.json());
        } catch (err) { console.error("Failed to fetch language groups", err); }
    };

    const fetchMasterLanguages = async () => {
        try {
            const res = await apiFetch('/api/master-languages');
            if (res.ok) setAvailableLanguages(await res.json());
        } catch (err) { console.error("Failed to fetch master languages", err); }
    };


    // --- Packaging Fetchers ---
    const fetchPackagingTypes = async () => {
        try {
            const res = await apiFetch('/api/master/packaging-types');
            if (res.ok) setPackagingTypes(await res.json());
        } catch (err) { console.error("Failed to fetch packaging types", err); }
    };

    const fetchPackagingGroups = async () => {
        try {
            const res = await apiFetch('/api/master/packaging-groups');
            if (res.ok) setPackagingGroups(await res.json());
        } catch (err) { console.error("Failed to fetch packaging groups", err); }
    };

    const fetchMasterPackagingProperties = async () => {
        try {
            const res = await apiFetch('/api/master/packaging-properties');
            if (res.ok) setAvailablePackagingProperties(await res.json());
        } catch (err) { console.error("Failed to fetch master packaging properties", err); }
    };

    const fetchPackagingProperties = async (sku, chargeId) => {
        try {
            let url = `/api/packaging-properties/${sku}?t=${Date.now()}`;
            if (chargeId) url += `&charge_id=${chargeId}`;
            const res = await apiFetch(url);
            if (res.ok) setPackagingProperties(await res.json());
        } catch (err) { console.error("Failed to fetch packaging properties", err); }
    };

    // --- CRUD Actions ---

    // Property Types
    const addPropertyType = async (data) => {
        await apiFetch('/api/master-property-types', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        fetchPropertyTypes();
    };
    const updatePropertyType = async (id, data) => {
        await apiFetch(`/api/master-property-types/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        fetchPropertyTypes();
    };
    const deletePropertyType = async (id) => {
        await apiFetch(`/api/master-property-types/${id}`, { method: 'DELETE' });
        fetchPropertyTypes();
    };



    // Master Properties
    const addMasterProperty = async (data) => {
        const isFormData = data instanceof FormData;
        await apiFetch('/api/master-properties', {
            method: 'POST',
            headers: {
                ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            },
            body: isFormData ? data : JSON.stringify(data)
        });
        fetchMasterProperties();
    };
    const updateMasterProperty = async (id, data) => {
        const isFormData = data instanceof FormData;
        await apiFetch(`/api/master-properties/${id}`, {
            method: 'PUT',
            headers: {
                ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            },
            body: isFormData ? data : JSON.stringify(data)
        });
        fetchMasterProperties();
    };
    const deleteMasterProperty = async (id) => {
        await apiFetch(`/api/master-properties/${id}`, { method: 'DELETE' });
        fetchMasterProperties();
    };

    // Language Groups
    const addLanguageGroup = async (data) => {
        await apiFetch('/api/master-language-groups', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        fetchLanguageGroups();
    };
    const updateLanguageGroup = async (id, data) => {
        await apiFetch(`/api/master-language-groups/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        fetchLanguageGroups();
    };
    const deleteLanguageGroup = async (id) => {
        await apiFetch(`/api/master-language-groups/${id}`, { method: 'DELETE' });
        fetchLanguageGroups();
    };

    // Master Languages
    const addMasterLanguage = async (data) => {
        await apiFetch('/api/master-languages', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        fetchMasterLanguages();
    };
    const updateMasterLanguage = async (id, data) => {
        await apiFetch(`/api/master-languages/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });
        fetchMasterLanguages();
    };
    const deleteMasterLanguage = async (id) => {
        await apiFetch(`/api/master-languages/${id}`, { method: 'DELETE' });
        fetchMasterLanguages();
        fetchMasterLanguages();
    };

    // --- Packaging CRUD ---
    // Packaging Types
    const addPackagingType = async (data) => {
        await apiFetch('/api/master/packaging-types', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
        fetchPackagingTypes();
    };
    const updatePackagingType = async (id, data) => {
        await apiFetch(`/api/master/packaging-types/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
        fetchPackagingTypes();
    };
    const deletePackagingType = async (id) => {
        await apiFetch(`/api/master/packaging-types/${id}`, { method: 'DELETE' });
        fetchPackagingTypes();
    };

    // Packaging Groups
    const addPackagingGroup = async (data) => {
        await apiFetch('/api/master/packaging-groups', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
        fetchPackagingGroups();
    };
    const updatePackagingGroup = async (id, data) => {
        await apiFetch(`/api/master/packaging-groups/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
        fetchPackagingGroups();
    };
    const deletePackagingGroup = async (id) => {
        await apiFetch(`/api/master/packaging-groups/${id}`, { method: 'DELETE' });
        fetchPackagingGroups();
    };

    // Master Packaging Properties
    const addMasterPackagingProperty = async (data) => {
        await apiFetch('/api/master/packaging-properties', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
        fetchMasterPackagingProperties();
    };
    const updateMasterPackagingProperty = async (id, data) => {
        await apiFetch(`/api/master/packaging-properties/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
        fetchMasterPackagingProperties();
    };
    const deleteMasterPackagingProperty = async (id) => {
        await apiFetch(`/api/master/packaging-properties/${id}`, { method: 'DELETE' });
        fetchMasterPackagingProperties();
    };

    // Product Packaging Properties
    const addPackagingProperty = async (data) => {
        if (!activeCharge) {
            toast.error("Please select a charge first.");
            return;
        }
        const payload = { ...data, product_sku: productData.sku, charge_id: activeCharge.id };
        await apiFetch('/api/packaging-properties', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        fetchPackagingProperties(productData.sku, activeCharge.id);
    };
    const updatePackagingProperty = async (id, data) => {
        if (!activeCharge) return;
        const payload = { ...data, charge_id: activeCharge.id };
        await apiFetch(`/api/packaging-properties/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (activeCharge) fetchPackagingProperties(productData.sku, activeCharge.id);
    };
    const deletePackagingProperty = async (id) => {
        if (!activeCharge) return;
        await apiFetch(`/api/packaging-properties/${id}?charge_id=${activeCharge.id}`, { method: 'DELETE' });
        if (activeCharge) fetchPackagingProperties(productData.sku, activeCharge.id);
    };

    // Fetch Colors
    const fetchColors = async () => {
        try {
            const res = await apiFetch('/api/colors');
            if (res.ok) {
                const data = await res.json();
                setColors(data);
            }
        } catch (err) {
            console.error("Failed to fetch colors", err);
        }
    };

    // Fetch Categories
    const fetchCategories = async () => {
        try {
            const res = await apiFetch('/api/categories');
            if (res.ok) {
                const data = await res.json();
                setCategories(data);
            }
        } catch (err) {
            console.error("Failed to fetch categories", err);
        }
    };

    // Fetch Statuses
    const fetchStatuses = async () => {
        try {
            const res = await apiFetch('/api/statuses');
            if (res.ok) {
                const data = await res.json();
                setStatuses(data);
            }
        } catch (err) {
            console.error("Failed to fetch statuses", err);
        }
    };



    const saveProduct = async (overrideData = null) => {
        try {
            let payload;

            if (overrideData) {
                payload = overrideData;
            } else {
                payload = {
                    sku: productData.sku,
                    model_name: productData.model,
                    ean: productData.ean,
                    description: productData.description,
                    notes: productData.notes,
                    color: productData.color,
                    category: productData.category,
                    factory_model: productData.factory_model,
                    status: productData.status,
                    is_accessory: productData.isAccessory,
                    product_types: productData.product_types,
                    image_url: productData.image
                };
            }

            const productId = overrideData ? overrideData.id : productData.id;
            let success = false;
            let resultData = null;
            let createPayload = null; // Scope: function-wide for saveProduct

            if (productId) {
                // UPDATE (PUT)
                const putRes = await apiFetch(`/api/products/${productId}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload)
                });

                if (putRes.ok) {
                    resultData = await putRes.json();
                    console.log("Product updated (PUT):", resultData);
                    success = true;
                }
            } else {
                // CREATE (POST)
                createPayload = overrideData ? payload : {
                    ...payload,
                    initial_charge: initialCharge.batch_number ? initialCharge : null
                };

                const postRes = await apiFetch('/api/products', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(createPayload)
                });

                if (postRes.ok) {
                    resultData = await postRes.json();
                    console.log("Product created (POST):", resultData);
                    success = true;
                    // Reset Initial Charge only if we used the state
                    if (!overrideData) {
                        setInitialCharge({ batch_number: '', status: 'Planned' });
                    }
                }
            }

            if (success) {
                // Refresh list and select the product
                const targetSku = payload.sku;

                // Fix for Auto-Loading Issue #2:
                // We force loadProducts to find this SKU even if it wasn't in the previous filter (unless filter strictly excludes it)
                // But more importantly, we override the current state to show this product clearly.
                await loadProducts(true, true, targetSku);

                // Fetch charges to ensure sync and SELECT the active charge
                if (targetSku) {
                    const charges = await fetchCharges(targetSku, false);
                    if (charges && charges.length > 0) {
                        // For a new product, we likely want the first charge (Initial Charge)
                        // If we just created it, it should be the only one or the first one.
                        // We check if we provided an initial charge (only for POST/create), and try to match it.
                        // Safe check for createPayload to avoid ReferenceError if scoping issues exist
                        if (typeof createPayload !== 'undefined' && createPayload && createPayload.initial_charge && createPayload.initial_charge.batch_number) {
                            console.log("Restoring initial charge from createPayload:", createPayload.initial_charge.batch_number);
                            const match = charges.find(c => c.batch_number === createPayload.initial_charge.batch_number);
                            if (match) setActiveCharge(match);
                            else setActiveCharge(charges[0]);
                        } else {
                            setActiveCharge(charges[0]);
                        }
                    }
                }

                return true;
            }

            console.error("Failed to save product");
            return false;
        } catch (err) {
            console.error("Error saving product:", err);
            return false;
        }
    };

    const addColor = async (color) => {
        try {
            const res = await apiFetch('/api/colors', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(color)
            });
            if (res.ok) {
                const newColor = await res.json();
                setColors([...colors, newColor]);
                return newColor;
            }
        } catch (err) {
            console.error("Failed to add color", err);
        }
    };

    const updateColor = async (id, color) => {
        try {
            const res = await apiFetch(`/api/colors/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(color)
            });
            if (res.ok) {
                setColors(colors.map(c => c.id === id ? { ...c, ...color } : c));
            }
        } catch (err) {
            console.error("Failed to update color", err);
        }
    };

    const deleteColor = async (id) => {
        try {
            const res = await apiFetch(`/api/colors/${id}`, { method: 'DELETE' });
            if (res.ok) {
                setColors(colors.filter(c => c.id !== id));
            }
        } catch (err) {
            console.error("Failed to delete color", err);
        }
    };

    const addCategory = async (category) => {
        try {
            const res = await apiFetch('/api/categories', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(category)
            });
            if (res.ok) {
                const newCategory = await res.json();
                setCategories([...categories, newCategory]);
                return newCategory;
            }
        } catch (err) {
            console.error("Failed to add category", err);
        }
    };

    const updateCategory = async (id, category) => {
        try {
            const res = await apiFetch(`/api/categories/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(category)
            });
            if (res.ok) {
                setCategories(categories.map(c => c.id === id ? { ...c, ...category } : c));
            }
        } catch (err) {
            console.error("Failed to update category", err);
        }
    };

    const deleteCategory = async (id) => {
        try {
            const res = await apiFetch(`/api/categories/${id}`, { method: 'DELETE' });
            if (res.ok) {
                setCategories(categories.filter(c => c.id !== id));
            }
        } catch (err) {
            console.error("Failed to delete category", err);
        }
    };

    const addStatus = async (status) => {
        try {
            const res = await apiFetch('/api/statuses', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(status)
            });
            if (res.ok) {
                const newStatus = await res.json();
                setStatuses([...statuses, newStatus]);
                return newStatus;
            }
        } catch (err) {
            console.error("Failed to add status", err);
        }
    };

    const updateStatus = async (id, status) => {
        try {
            const res = await apiFetch(`/api/statuses/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(status)
            });
            if (res.ok) {
                setStatuses(statuses.map(s => s.id === id ? { ...s, ...status } : s));
            }
        } catch (err) {
            console.error("Failed to update status", err);
        }
    };

    const deleteStatus = async (id) => {
        try {
            const res = await apiFetch(`/api/statuses/${id}`, { method: 'DELETE' });
            if (res.ok) {
                setStatuses(statuses.filter(s => s.id !== id));
            }
        } catch (err) {
            console.error("Failed to delete status", err);
        }
    };

    // Fetch Properties
    const fetchProperties = async (sku, chargeId) => {
        try {
            let url = `/api/properties/${sku}?t=${Date.now()}`;
            if (chargeId) url += `&charge_id=${chargeId}`;

            const res = await apiFetch(url);
            if (res.ok) {
                const data = await res.json();
                setProperties(data);
            }
        } catch (err) {
            console.error("Failed to fetch properties", err);
        }
    };

    // Fetch Charge Languages
    const fetchChargeLanguages = async (sku, chargeId) => {
        try {
            let url = `/api/charge-languages/${sku}?t=${Date.now()}`; // Add timestamp to prevent caching
            if (chargeId) url += `&charge_id=${chargeId}`;

            const res = await apiFetch(url);
            if (res.ok) {
                const data = await res.json();
                setChargeLanguages(data);
            }
        } catch (err) {
            console.error("Failed to fetch charge languages", err);
        }
    };

    // Fetch Product Images
    const fetchProductImages = async (sku) => {
        try {
            const res = await apiFetch(`/api/products/${sku}/images`);
            if (res.ok) {
                const data = await res.json();
                setProductImages(data);
            }
        } catch (err) {
            console.error("Failed to fetch product images", err);
        }
    };

    // --- Software & Resources ---
    // --- Software & Resources ---
    const fetchChargeDocuments = useCallback(async (chargeId) => {
        try {
            const res = await apiFetch(`/api/charge-documents/${productData.sku}?charge_id=${chargeId}`);
            if (res.ok) {
                const allDocs = await res.json();
                setAllChargeDocuments(allDocs);
            }
        } catch (err) { console.error("Failed to fetch charge documents", err); }
    }, [productData.sku]);

    const fetchResourceTypes = async () => {
        try {
            const res = await apiFetch('/api/master/resource-types');
            if (res.ok) setResourceTypes(await res.json());
        } catch (err) { console.error("Failed to fetch resource types", err); }
    };

    // Deprecated but kept for compatibility if needed (mapped to new fetcher)


    const addResourceType = async (data) => {
        await apiFetch('/api/master/resource-types', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
        fetchResourceTypes(); // Refresh all
    };
    const updateResourceType = async (id, data) => {
        await apiFetch(`/api/master/resource-types/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
        fetchResourceTypes();
    };
    const deleteResourceType = async (id) => {
        await apiFetch(`/api/master/resource-types/${id}`, { method: 'DELETE' });
        fetchResourceTypes();
    };

    const uploadWithProgress = (url, formData, onProgress) => {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', url);
            xhr.setRequestHeader('Authorization', `Bearer ${localStorage.getItem('token')}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable && onProgress) {
                    const percentComplete = (event.loaded / event.total) * 100;
                    onProgress(percentComplete);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 503 && xhr.responseText === "NEXTCLOUD_CONNECTION_LOST") {
                    toast.error("Upload failed: No connection to Nextcloud storage. Please check if Drive Z: is mounted.");
                    reject(new Error("Nextcloud Disconnected"));
                    return;
                }
                if (xhr.status >= 200 && xhr.status < 300) {
                    const response = JSON.parse(xhr.responseText);
                    if (response.warning) {
                        toast.warning(response.warning);
                    }
                    resolve(response);
                } else {
                    reject(new Error(xhr.statusText));
                }
            };

            xhr.onerror = () => reject(new Error('Network Error'));
            xhr.send(formData);
        });
    };

    const addSoftwareResource = async (formData, onProgress) => {
        if (!activeCharge) return;
        formData.append('charge_id', activeCharge.id);
        if (!formData.get('type')) formData.append('type', 'Software');
        if (!formData.get('scope')) formData.append('scope', 'charge');
        formData.append('folder', 'software');

        await uploadWithProgress('/api/charge-documents', formData, onProgress);
        fetchChargeDocuments(activeCharge.id);
    };

    const updateSoftwareResource = async (id, data) => {
        if (!activeCharge) return;
        await apiFetch(`/api/charge-documents/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
        fetchChargeDocuments(activeCharge.id);
    };

    const deleteSoftwareResource = async (id) => {
        if (!activeCharge) return;
        await apiFetch(`/api/charge-documents/${id}`, { method: 'DELETE' });
        fetchChargeDocuments(activeCharge.id);
    };

    const addDocumentResource = async (formData, onProgress) => {
        if (!activeCharge) return;
        formData.append('charge_id', activeCharge.id);
        if (!formData.get('type')) formData.append('type', 'Document');
        if (!formData.get('scope')) formData.append('scope', 'charge');
        formData.append('folder', 'documents');

        await uploadWithProgress('/api/charge-documents', formData, onProgress);
        fetchChargeDocuments(activeCharge.id);
    };

    const updateDocumentResource = async (id, data) => {
        if (!activeCharge) return;
        await apiFetch(`/api/charge-documents/${id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
        fetchChargeDocuments(activeCharge.id);
    };

    const deleteDocumentResource = async (id) => {
        if (!activeCharge) return;
        await apiFetch(`/api/charge-documents/${id}`, { method: 'DELETE' });
        fetchChargeDocuments(activeCharge.id);
    };

    // Refetch data when activeCharge changes
    useEffect(() => {
        if (productData.sku) {
            // fetchPackagingProperties(productData.sku); // Moved to below with activeCharge
            fetchProductImages(productData.sku);
        }
        if (productData.sku && activeCharge) {
            fetchProperties(productData.sku, activeCharge.id);
            fetchPackagingProperties(productData.sku, activeCharge.id);
            fetchChargeLanguages(productData.sku, activeCharge.id);
            fetchChargeLanguages(productData.sku, activeCharge.id);
            fetchChargeDocuments(activeCharge.id);
        }
    }, [activeCharge, productData.sku, fetchChargeDocuments]);

    const addProperty = async (prop) => {
        if (!activeCharge) {
            toast.error("Please select a charge first.");
            return;
        }
        try {
            const payload = { ...prop, product_sku: productData.sku, charge_id: activeCharge.id };
            const res = await apiFetch('/api/properties', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                const responseData = await res.json();
                // If it already existed, we might want to update our local state to ensure we have the correct ID
                // But for now, just adding it to the list if it's not there is fine, or refetching.
                // To be safe against duplicates in UI, let's check if it's already in state
                setProperties(prev => {
                    if (prev.some(p => p.name === payload.name)) return prev;
                    return [...prev, { ...payload, id: responseData.id }];
                });
            }
        } catch (err) {
            console.error("Failed to add property", err);
        }
    };

    const updateProperty = async (id, updates) => {
        try {
            // Optimistic update
            setProperties(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));

            const res = await apiFetch(`/api/properties/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updates)
            });

            if (!res.ok) {
                console.error("Failed to update property on server");
                // Revert if needed, or just fetchProperties
                if (activeCharge) fetchProperties(productData.sku, activeCharge.id);
            }
        } catch (err) {
            console.error("Failed to update property", err);
        }
    };

    const deleteProperty = async (id) => {
        try {
            // Optimistic update
            setProperties(prev => prev.filter(p => p.id !== id));

            const res = await apiFetch(`/api/properties/${id}`, { method: 'DELETE' });

            if (!res.ok) {
                console.error("Failed to delete property on server");
                if (activeCharge) fetchProperties(productData.sku, activeCharge.id);
            }
        } catch (err) {
            console.error("Failed to delete property", err);
        }
    };

    const addChargeLanguage = async (languageId) => {
        if (!activeCharge) {
            toast.error("Please select a charge first.");
            return;
        }
        try {
            const payload = { product_sku: productData.sku, charge_id: activeCharge.id, language_id: languageId };
            const res = await apiFetch('/api/charge-languages', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                // Refetch languages
                fetchChargeLanguages(productData.sku, activeCharge.id);
            }
        } catch (err) {
            console.error("Failed to add charge language", err);
        }
    };

    const updateChargeLanguage = async (id, updates) => {
        try {
            const res = await apiFetch(`/api/charge-languages/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updates)
            });
            if (res.ok) {
                fetchChargeLanguages(productData.sku, activeCharge.id);
            }
        } catch (err) {
            console.error("Failed to update charge language", err);
        }
    };

    const deleteChargeLanguage = async (idOrObj) => {
        console.log("deleteChargeLanguage called with:", idOrObj);

        // Determine ID for optimistic update
        let idToDelete;
        if (typeof idOrObj === 'object') {
            idToDelete = idOrObj.id;
        } else {
            idToDelete = idOrObj;
        }
        console.log("ID to delete:", idToDelete);

        // Optimistic update: Remove from UI immediately
        if (idToDelete) {
            console.log("Performing optimistic update for ID:", idToDelete);
            setChargeLanguages(prev => {
                const newLanguages = prev.filter(l => l.id != idToDelete); // Loose comparison
                console.log("New languages count:", newLanguages.length);
                return newLanguages;
            });
        }

        try {
            let url;
            // Prefer ID if available and not an object (or if object has id)
            if (typeof idOrObj === 'object' && idOrObj.id) {
                url = `/api/charge-languages/${idOrObj.id}`;
            } else if (typeof idOrObj === 'object') {
                const { charge_id, language_id } = idOrObj;
                url = `/api/charge-languages?charge_id=${charge_id}&language_id=${language_id}`;
            } else {
                url = `/api/charge-languages/${idOrObj}`;
            }

            const res = await apiFetch(url, { method: 'DELETE' });

            if (!res.ok) {
                const msg = await res.text();
                throw new Error(msg || 'Failed to delete charge language');
            }

            const json = await res.json();
            console.log("deleteChargeLanguage success:", json);

            // Fetch to ensure sync
            fetchChargeLanguages(productData.sku, activeCharge.id);
        } catch (err) {
            console.error("Failed to delete charge language", err);
            // Revert optimistic update if failed
            toast.error("Failed to delete language. It may reappear on refresh.");
            fetchChargeLanguages(productData.sku, activeCharge.id);
        }
    };

    // Fetch Charges
    // Fetch Charges
    const fetchCharges = async (identifier, isModel = false) => {
        if (!identifier) return [];
        try {
            const param = isModel ? 'model' : 'sku';
            const res = await apiFetch(`/api/charges?${param}=${identifier}&t=${Date.now()}`);
            if (res.ok) {
                const data = await res.json();
                // Map batch_number to charge_id for frontend compatibility
                const mappedData = data.map(c => ({
                    ...c,
                    charge_id: c.batch_number || c.charge_id // Use batch_number if available, fallback for safety
                }));
                setCharges(mappedData);

                // Set active charge logic
                if (mappedData.length > 0) {
                    if (activeCharge) {
                        const stillExists = mappedData.find(c => c.id === activeCharge.id);
                        if (stillExists) {
                            setActiveCharge(stillExists);
                        } else {
                            setActiveCharge(mappedData[0]);
                        }
                    } else {
                        setActiveCharge(mappedData[0]);
                    }
                } else {
                    setActiveCharge(null);
                }
                return mappedData;
            }
        } catch (err) {
            console.error("Failed to fetch charges", err);
        }
        return [];
    };

    const addCharge = async (charge) => {
        console.log("addCharge called with:", charge);
        try {
            // Map charge_id back to batch_number for backend
            const payload = {
                ...charge,
                batch_number: charge.charge_id,
                product_sku: productData.sku
            };
            const res = await apiFetch('/api/charges', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                const result = await res.json();
                console.log("addCharge success, result:", result);

                // Refetch to get the full object
                let newCharges = await fetchCharges(productData.sku, false);

                // Set the new charge as active
                if (newCharges && newCharges.length > 0) {
                    const newCharge = newCharges.find(c => c.id === result.id) || newCharges[0];
                    setActiveCharge(newCharge);
                }
            }
        } catch (err) {
            console.error("Failed to add charge", err);
        }
    };

    const updateCharge = async (id, charge) => {
        try {
            // Map charge_id back to batch_number for backend
            const payload = {
                ...charge,
                batch_number: charge.charge_id
            };
            const res = await apiFetch(`/api/product-charges/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                // Refetch to update list
                await fetchCharges(productData.sku, false);
            } else {
                const msg = res.status === 404 ? "Endpoint not found. Please restart the backend server." : `Server error: ${res.status}`;
                toast.error(`Failed to update charge: ${msg}`);
                console.error("Update failed:", res.status, res.statusText);
            }
        } catch (err) {
            console.error("Failed to update charge", err);
            toast.error(`Error updating charge: ${err.message}`);
        }
    };

    const deleteCharge = async (id) => {
        try {
            const res = await apiFetch(`/api/charges/${id}`, { method: 'DELETE' });
            if (res.ok) {
                // Refetch to update list
                const newCharges = await fetchCharges(productData.sku, false);

                // If active charge was deleted, reset active charge
                if (activeCharge && activeCharge.id === id) {
                    setActiveCharge(newCharges.length > 0 ? newCharges[0] : null);
                }
            } else {
                const msg = res.status === 404 ? "Endpoint not found. Please restart the backend server." : `Server error: ${res.status}`;
                toast.error(`Failed to delete charge: ${msg}`);
                console.error("Delete failed:", res.status, res.statusText);
            }
        } catch (err) {
            console.error("Failed to delete charge", err);
            toast.error(`Error deleting charge: ${err.message}`);
        }
    };

const fetchProduct = async (sku) => {
        try {
            const res = await apiFetch(`/api/products/${sku}`);
            if (res.ok) {
                const data = await res.json();
                console.log("fetchProduct data:", data); // Debug log

                // Ensure we map all fields correctly based on DB columns
                setProductData({
                    id: data.id, // Capture internal ID
                    model: data.model_name || data.model || '', // Handle potential variations
                    sku: data.sku || '',
                    ean: data.ean || '',
                    description: data.description || '',
                    notes: data.notes || '',
                    color: data.color || '',
                    category: data.category || '',
                    factory_model: data.factory_model || '', // New Field
                    status: data.status || '',
                    stock: data.stock_level || 0, // DB column might be stock_level
                    isAccessory: data.is_accessory === 1 || data.is_accessory === true,
                    product_types: typeof data.product_types === 'string' ? JSON.parse(data.product_types) : (data.product_types || []),
                    image: data.image_url || ''
                });

                // Fetch charges by Product SKU
                fetchCharges(data.sku, false);
            }
        } catch (err) {
            console.error("Failed to fetch product", err);
        }
    };
    // Search & Navigation State
    const [loadedProducts, setLoadedProducts] = useState([]);
    const [currentProductIndex, setCurrentProductIndex] = useState(-1);
    const [lastSearchParams, setLastSearchParams] = useState(() => {
        const saved = sessionStorage.getItem('lastSearchParams');
        return saved ? JSON.parse(saved) : null;
    });

    // Load Products (Search)
    const loadProducts = async (useLastParams = false, preserveCurrent = false, targetSku = null) => {
        try {
            let searchCriteria = {};

            if (useLastParams) {
                if (!lastSearchParams) {
                    // If we want to use last params but none exist (e.g. after clear), do nothing or return empty
                    // This prevents re-loading default list
                    console.log("No last params to load.");
                    setLoadedProducts([]);
                    setCurrentProductIndex(-1);
                    return;
                }
                searchCriteria = lastSearchParams;
            } else {
                // Capture current form state as search criteria
                searchCriteria = {
                    sku: productData.sku,
                    model: productData.model,
                    color: productData.color,
                    status: productData.status,
                    category: productData.category,
                    isAccessory: productData.isAccessory,
                    product_types: productData.product_types || []
                };
                setLastSearchParams(searchCriteria);
                sessionStorage.setItem('lastSearchParams', JSON.stringify(searchCriteria));
            }

            const params = new URLSearchParams();
            if (searchCriteria.sku) params.append('sku', searchCriteria.sku);
            if (searchCriteria.model) params.append('model', searchCriteria.model);
            if (searchCriteria.color) params.append('color', searchCriteria.color);
            if (searchCriteria.status) params.append('status', searchCriteria.status);
            if (searchCriteria.category) params.append('category', searchCriteria.category);
            if (searchCriteria.isAccessory) params.append('is_accessory', 'true');
            if (searchCriteria.product_types && searchCriteria.product_types.length > 0) {
                params.append('product_types', JSON.stringify(searchCriteria.product_types));
            }

            console.log("Fetching products with params:", params.toString());
            const res = await apiFetch(`/api/products?${params.toString()}`);

            if (res.ok) {
                const data = await res.json();
                console.log("Loaded products:", data.length);
                setLoadedProducts(data);

                if (data.length > 0) {
                    // If we are refreshing (useLastParams=true), try to stay on current index or closest
                    let newIndex = 0;
                    let shouldFetch = true;

                    // Check if the currently loaded product is in the new list
                    const skuToFind = targetSku || productData.sku;
                    const currentSkuIndex = data.findIndex(p => p.sku === skuToFind);

                    if (preserveCurrent && currentSkuIndex >= 0) {
                        newIndex = currentSkuIndex;
                        shouldFetch = false; // Don't re-fetch if we are preserving current
                    } else if (currentSkuIndex >= 0) {
                        // Even if not explicitly preserving, if the current product is in the list, stay on it
                        newIndex = currentSkuIndex;
                        shouldFetch = false;
                    } else if (targetSku) {
                        // If we have a specific target SKU (e.g. from URL) and it's NOT in the list
                        // We should NOT fetch a different product. We should just keep the current one.
                        newIndex = -1;
                        shouldFetch = false;
                    } else if (useLastParams && currentProductIndex >= 0) {
                        // If we deleted the last item, go to previous
                        if (currentProductIndex >= data.length) {
                            newIndex = data.length - 1;
                        } else {
                            newIndex = currentProductIndex;
                        }
                    } else if (useLastParams && lastSearchParams) { // Check that we actually have params to restore from
                        // Try to restore index from sessionStorage if available
                        const savedIndex = sessionStorage.getItem('currentProductIndex');
                        if (savedIndex !== null) {
                            newIndex = parseInt(savedIndex, 10);
                            if (newIndex >= data.length) newIndex = data.length - 1;
                        }
                    }

                    setCurrentProductIndex(newIndex);
                    sessionStorage.setItem('currentProductIndex', newIndex);

                    if (shouldFetch && data[newIndex]) {
                        // Load the product data for the new index
                        await fetchProduct(data[newIndex].sku);
                    }
                } else {
                    setLoadedProducts([]); // Explicitly set empty
                    setCurrentProductIndex(-1);
                    sessionStorage.removeItem('currentProductIndex');
                    if (!useLastParams && !preserveCurrent) toast.info("No products found matching criteria.");
                }
            }
        } catch (err) {
            console.error("Failed to load products", err);
        }
    };

    // Restore state on mount and fetch master data
    const restoredRef = React.useRef(false);

    useEffect(() => {
        if (user) {
            // Fetch Master Data
            fetchMasterProperties();
            fetchPropertyGroups();
            fetchPropertyTypes();
            fetchLanguageGroups();
            fetchMasterLanguages();
            fetchColors();
            fetchCategories();
            fetchStatuses();
            fetchResourceTypes();

            // Fetch Packaging Data
            fetchMasterPackagingProperties();
            fetchPackagingGroups();
            fetchPackagingTypes();

            const params = new URLSearchParams(window.location.search);
            const skuParam = params.get('sku');
            const chargeIdParam = params.get('charge_id');

            if (skuParam && !restoredRef.current) {
                console.log("Restoring from URL params:", skuParam, chargeIdParam);
                restoredRef.current = true;

                fetchProduct(skuParam).then(async () => {
                    const charges = await fetchCharges(skuParam, false);
                    if (charges && charges.length > 0 && chargeIdParam) {
                        const targetCharge = charges.find(c => c.id == chargeIdParam);
                        if (targetCharge) {
                            setActiveCharge(targetCharge);
                        }
                    }
                    // Also load the list in background so navigation works, but don't override current product
                    loadProducts(true, true, skuParam); // New flag: preserveCurrent, and targetSku
                });
            } else if (lastSearchParams && !restoredRef.current) {
                console.log("Restoring last search state...");
                loadProducts(true);
                restoredRef.current = true;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Navigation
    const nextProduct = () => {
        if (loadedProducts.length === 0) return;
        let currentIndex = parseInt(currentProductIndex, 10);
        let newIndex = currentIndex + 1;
        if (newIndex >= loadedProducts.length) {
            newIndex = 0; // Loop to start
        }
        setCurrentProductIndex(newIndex);
        sessionStorage.setItem('currentProductIndex', newIndex);
        fetchProduct(loadedProducts[newIndex].sku);
    };

    const prevProduct = () => {
        if (loadedProducts.length === 0) return;
        let currentIndex = parseInt(currentProductIndex, 10);
        let newIndex = currentIndex - 1;
        if (newIndex < 0) {
            newIndex = loadedProducts.length - 1; // Loop to end
        }
        setCurrentProductIndex(newIndex);
        sessionStorage.setItem('currentProductIndex', newIndex);
        fetchProduct(loadedProducts[newIndex].sku);
    };

    // --- URL Synchronization ---
    useEffect(() => {
        if (!productData.sku) return;

        const params = new URLSearchParams(window.location.search);
        let updated = false;

        if (params.get('sku') !== productData.sku) {
            params.set('sku', productData.sku);
            updated = true;
        }

        if (activeCharge) {
            if (params.get('charge_id') !== String(activeCharge.id)) {
                params.set('charge_id', activeCharge.id);
                updated = true;
            }
        } else {
            if (params.has('charge_id')) {
                params.delete('charge_id');
                updated = true;
            }
        }

        if (updated) {
            const newUrl = `${window.location.pathname}?${params.toString()}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, [productData.sku, activeCharge]);

    // CRUD Operations
    const newProduct = () => {
        setProductData({
            id: null,
            model: '',
            sku: '',
            ean: '',
            description: '',
            notes: '',
            color: '',
            category: '',
            factory_model: '',
            status: '',
            stock: 0,
            isAccessory: false,
            product_types: [],
            image: null
        });
        setActiveCharge(null);
        setCharges([]);
        setProperties([]);
        setChargeLanguages([]);
        setProductImages([]);
        setAllChargeDocuments([]);
        setAccessories([]);
        setComponents([]); // FIX: Clear components as well (affects Package contents)
    };

    const clearProduct = () => {
        newProduct();
        setLoadedProducts([]);
        setCurrentProductIndex(-1);
        sessionStorage.removeItem('lastSearchParams');

        // Force clear URL to prevent reload restoration
        window.history.replaceState({}, '', window.location.pathname);
    };

    const copyProduct = async (options = {}) => {
        if (!productData.id) {
            toast.error("Please select a product to copy.");
            return false;
        }

        try {
            const res = await apiFetch(`/api/products/${productData.id}/copy`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    activeChargeId: activeCharge?.id,
                    sourceChargeId: options.sourceChargeId,
                    targetChargeId: options.targetChargeId,
                    ...options
                })
            });

            if (res.ok) {
                const data = await res.json();

                if (options.targetMode === 'existing') {
                    toast.success(`Data copied to existing product successfully!`);
                    // If we copied to a different product, we might want to switch to it, or just reload
                    // For now, let's reload to ensure data is fresh if we are viewing the target
                    await loadProducts(true);
                    if (options.targetProductId) {
                        // Find SKU of target product to load it
                        const targetP = loadedProducts.find(p => p.id === options.targetProductId);
                        if (targetP) await fetchProduct(targetP.sku);
                    }
                } else {
                    const newSku = data.newSku;
                    toast.success(`Product copied successfully! New SKU: ${newSku}`);
                    // Reload products and select the new one
                    await loadProducts(true);
                    // Fetch and select the new product using the actual SKU returned by backend
                    await fetchProduct(newSku);
                }
                return true;
            } else {
                const msg = await res.text();
                toast.error(`Failed to copy product: ${msg}`);
                return false;
            }
        } catch (err) {
            console.error("Error copying product:", err);
            toast.error("Error copying product.");
            return false;
        }
    };

    const deleteProduct = async () => {
        console.log("deleteProduct initiated");
        console.log("Current productData:", productData);

        if (!productData || !productData.sku) {
            console.warn("No SKU found in productData");
            toast.error("No product selected to delete. Please load a product first.");
            return;
        }

        // Confirmation is now handled by the UI component (App.jsx)
        try {
            console.log(`Sending DELETE request for ${productData.sku}...`);
            const res = await apiFetch(`/api/products/${productData.sku}`, { method: 'DELETE' });

            console.log("DELETE response status:", res.status);

            if (res.ok) {
                toast.success("Product deleted and removed from list.");
                await loadProducts(true);
            } else if (res.status === 404) {
                toast.error("Product not found (it may have already been deleted).");
            } else {
                console.error("Delete failed:", res.status);
                toast.error(`Failed to delete product. Server returned ${res.status}`);
            }
        } catch (err) {
            console.error("Failed to delete product (exception):", err);
            toast.error(`Error deleting product: ${err.message}`);
        }
    };

    // Accessories
    const [accessories, setAccessories] = useState([]);
    const [potentialAccessories, setPotentialAccessories] = useState([]);

    const fetchAccessories = async (productId) => {
        try {
            const res = await apiFetch(`/api/products/${productId}/accessories`);
            if (res.ok) setAccessories(await res.json());
        } catch (err) { console.error("Failed to fetch accessories", err); }
    };

    const fetchPotentialAccessories = async () => {
        try {
            const res = await apiFetch('/api/accessories');
            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data)) {
                    setPotentialAccessories(data);
                } else {
                    console.error("fetchPotentialAccessories: received non-array data", data);
                    setPotentialAccessories([]);
                }
            } else {
                setPotentialAccessories([]);
            }
        } catch (err) {
            console.error("Failed to fetch potential accessories", err);
            setPotentialAccessories([]);
        }
    };

    const addAccessory = async (productId, accessoryId, accessoryChargeId = null) => {
        try {
            const res = await apiFetch(`/api/products/${productId}/accessories`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ accessory_id: accessoryId, is_package_content: 0, accessory_charge_id: accessoryChargeId })
            });
            if (res.ok) fetchAccessories(productId);
        } catch (err) { console.error("Failed to add accessory", err); }
    };

    const updateAccessory = async (productId, accessoryId, data) => {
        try {
            const res = await apiFetch(`/api/products/${productId}/accessories/${accessoryId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            if (res.ok) fetchAccessories(productId);
        } catch (err) { console.error("Failed to update accessory", err); }
    };

    const deleteAccessory = async (productId, accessoryId) => {
        try {
            const res = await apiFetch(`/api/products/${productId}/accessories/${accessoryId}`, { method: 'DELETE' });
            if (res.ok) fetchAccessories(productId);
        } catch (err) { console.error("Failed to delete accessory", err); }
    };

    // --- COMPONENTS ---
    const fetchComponents = async (productId) => {
        try {
            const res = await apiFetch(`/api/products/${productId}/components`);
            if (res.ok) {
                const data = await res.json();
                if (Array.isArray(data)) {
                    setComponents(data);
                } else {
                    console.error("fetchComponents: received non-array data", data);
                    setComponents([]);
                }
            } else {
                console.error("Failed to fetch components, status:", res.status);
                // Don't clear components on error? Or do? 
                // Better to keep stale data or show empty? 
                // If error, probably better to not overwrite with empty if we had data, 
                // but if it's a fresh load, we want empty.
                // For now, let's just log. If we don't setComponents, it stays as is (or empty).
            }
        } catch (err) { console.error("Failed to fetch components", err); }
    };

    const addComponent = async (productId, componentId, role, chargeId, notes) => {
        try {
            const res = await apiFetch(`/api/products/${productId}/components`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ component_id: componentId, role, charge_id: chargeId, notes })
            });
            if (res.ok) fetchComponents(productId);
            return res.ok;
        } catch (err) {
            console.error("Failed to add component", err);
            return false;
        }
    };

    const deleteComponent = async (productId, componentRowId) => {
        try {
            const res = await apiFetch(`/api/products/${productId}/components/${componentRowId}`, { method: 'DELETE' });
            if (res.ok) fetchComponents(productId);
        } catch (err) {
            console.error("Failed to delete component", err);
        }
    };

    const updateComponent = async (productId, componentRowId, updates) => {
        try {
            const res = await apiFetch(`/api/products/${productId}/components/${componentRowId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updates)
            });
            if (res.ok) fetchComponents(productId);
            return res.ok;
        } catch (err) {
            console.error("Failed to update component", err);
            return false;
        }
    };

    useEffect(() => {
        if (productData.id) {
            fetchAccessories(productData.id);
            fetchComponents(productData.id);
        }
    }, [productData.id]);


    // --- USER MANAGEMENT ---
    const [users, setUsers] = useState([]);


    const fetchUsers = async () => {
        try {
            const res = await apiFetch('/api/users');
            if (res.ok) setUsers(await res.json());
        } catch (err) { console.error("Error fetching users:", err); }
    };

    const addUser = async (userData) => {
        try {
            const res = await apiFetch('/api/users', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(userData)
            });
            if (res.ok) { fetchUsers(); return { success: true }; }
            return { success: false, message: await res.text() };
        } catch (err) { return { success: false, message: err.message }; }
    };

    const updateUser = async (id, userData) => {
        try {
            const res = await apiFetch(`/api/users/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(userData)
            });
            if (res.ok) { fetchUsers(); return { success: true }; }
            return { success: false, message: await res.text() };
        } catch (err) { return { success: false, message: err.message }; }
    };

    const deleteUser = async (id) => {
        try {
            const res = await apiFetch(`/api/users/${id}`, { method: 'DELETE' });
            if (res.ok) { fetchUsers(); return { success: true }; }
            return { success: false, message: await res.text() };
        } catch (err) { return { success: false, message: err.message }; }
    };

    const userRole = (user?.role || '').toLowerCase();
    const isAdmin = userRole === 'admin';
    const isEditor = userRole === 'editor' || isAdmin;
    const isViewer = userRole === 'viewer';
    const canManageSettings = isAdmin;
    const canEditContent = isEditor;
    const canExport = isEditor;

    // --- SETTINGS & THEMING ---
    const [settings, setSettings] = useState({});



    const fetchSettings = useCallback(async () => {
        try {
            const res = await apiFetch('/api/settings');
            if (res.ok) {
                const data = await res.json();
                setSettings(data);
                applyTheme(data);
            }
        } catch (err) { console.error("Error fetching settings:", err); }
    }, []);

    const updateSettings = async (newSettings) => {
        try {
            const res = await apiFetch('/api/settings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(newSettings)
            });
            if (res.ok) { fetchSettings(); return { success: true }; }
            return { success: false, message: await res.text() };
        } catch (err) { return { success: false, message: err.message }; }
    };



    useEffect(() => {
        if (dbMode === 'test') { applyTheme(TEST_THEME); }
        else if (settings && Object.keys(settings).length > 0) { applyTheme(settings); }
    }, [dbMode, settings]);

    useEffect(() => { fetchSettings(); }, [fetchSettings]);

    return (
        <PIMContext.Provider value={{
            settings, updateSettings,
            activeTab, setActiveTab,
            user, logout,
            properties, setProperties, fetchProperties,
            charges, setCharges,
            colors, setColors,
            categories, setCategories,
            statuses, setStatuses,
            addStatus, updateStatus, deleteStatus,
            addCategory, updateCategory, deleteCategory,
            addColor, updateColor, deleteColor,
            addLanguageGroup, updateLanguageGroup, deleteLanguageGroup,
            addMasterLanguage, updateMasterLanguage, deleteMasterLanguage,
            addMasterProperty, updateMasterProperty, deleteMasterProperty,
            availableProperties, propertyGroups, propertyTypes,
            languageGroups, availableLanguages,
            packagingProperties, availablePackagingProperties, packagingGroups, packagingTypes,
            addPackagingType, updatePackagingType, deletePackagingType,
            productData, setProductData,
            activeCharge, setActiveCharge,
            chargeLanguages, setChargeLanguages,
            addChargeLanguage, updateChargeLanguage, deleteChargeLanguage,
            addCharge, updateCharge, deleteCharge,
            addProperty, updateProperty, deleteProperty,
            softwareResources, documentResources, resourceTypes,
            addResourceType, updateResourceType, deleteResourceType,
            addSoftwareResource, updateSoftwareResource, deleteSoftwareResource,
            addDocumentResource, updateDocumentResource, deleteDocumentResource,
            productImages, setProductImages, fetchProductImages,
            addPackagingGroup, deletePackagingGroup, updatePackagingGroup,
            addMasterPackagingProperty, deleteMasterPackagingProperty, updateMasterPackagingProperty,
            fetchPackagingProperties,
            addPackagingProperty, deletePackagingProperty, updatePackagingProperty,
            fetchDashboardOverview,

            // Dynamic Widgets
            leftWidget, setLeftWidget,
            middleWidget, setMiddleWidget,
            rightWidget, setRightWidget,
            selectedPalletArrangement, setSelectedPalletArrangement,
            palletCalculationResults, setPalletCalculationResults,
            isPalletMode, setIsPalletMode,
            palletMiddleWidget, setPalletMiddleWidget,
            palletRightWidget, setPalletRightWidget,
            resetLayout,

            saveProduct,
            loadProducts, loadedProducts, currentProductIndex, nextProduct, prevProduct,
            newProduct, clearProduct, copyProduct, deleteProduct, fetchProduct,

            addPropertyType, deletePropertyType, updatePropertyType,
            addPropertyGroup, deletePropertyGroup, updatePropertyGroup,

            // COMPONENTS (New)
            components, setComponents,
            fetchComponents, addComponent, deleteComponent, updateComponent,

            // User Management
            users, fetchUsers,
            currentUser: user, setCurrentUser: setUser,
            login, authLoading,
            addUser, updateUser, deleteUser,
            canManageSettings, canEditContent, canExport, isAdmin, isEditor, isViewer,

            // DB Mode
            dbMode, fetchDbMode,
            isInnerPackMode, setIsInnerPackMode,
            innerPackCalculationResult, setInnerPackCalculationResult,

            // Legacy / Accessories
            accessories, fetchAccessories,
            potentialAccessories, fetchPotentialAccessories,
            addAccessory, updateAccessory, deleteAccessory,
            
            // Global Ring State
            ringState, updateRingState
        }}>
            {children}
        </PIMContext.Provider>
    );
};
